import {
  ButtonType,
  ButtonWidget,
  TextFieldWidget,
  TextLinkWidget,
} from '@tableau/widgets';
import { DataGrid } from '@tableau/widgets-datagrid';
import { Formik, FormikProps, useFormikContext } from 'formik';
import * as React from 'react';

import { ExtensionsSettingsSubspaceState } from '~src/app/spaces/SharedSettingsSpace/ExtensionsSettingsSubspaceShared';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { useSettingsCoordinator } from '~src/settings/components/settings-coordinator/SettingsCoordinatorHook';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { Messages } from '../../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../../commonInfra/components/FormikCheckbox';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { wideField } from '../../../styles/site-name-and-id/SiteNameAndIdStyles';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { ExtensionsSettingsSubFormikProps } from '../ExtensionsSettingsCoordinator';
import {
  addButtonInfoStyle,
  addButtonStyle,
  buttonStyle,
  dataGridBlockContainerStyle,
  dataGridContainerStyle,
  dataGridRowHeight,
  sectionHeader,
  textFieldStyle
} from './ExtensionsSectionStyle';
import {
  ADD_BLOCK_URL_BUTTON_TEST_ID,
  ADD_BLOCK_URL_TOOLTIP_TEST_ID,
  BLOCK_URL_INPUT_PREFIX_TEST_ID,
  BLOCKLIST_EMPTY_TEST_ID,
  DELETE_BLOCK_URL_BUTTON_PREFIX_TEST_ID,
  SERVER_EXTENSIONS_ENABLED_TEST_ID,
  SERVER_EXTENSIONS_LEARN_MORE_LINK_TEST_ID,
  SERVER_EXTENSIONS_SECTION_TEST_ID,
} from './ExtensionsTestIds';
import { QuickTooltip } from './QuickTooltip';

export const EXTENSIONS_ENABLED_FIELD = 'settings.dashExtServerSettings.extensionsGloballyEnabled';

const DEFAULT_URL = 'https://';

export interface ExtensionsBlock {
  blockUrl: string;
}

export type ExtensionsServerFormField = { blocklist: ExtensionsBlock[] }
export type ExtensionsServerFormikProps = FormikProps<ExtensionsServerFormField>;

export function ExtensionsServerSection(props: ExtensionsSettingsSubFormikProps) {
  const [blocklist, setBlocklist] = React.useState<ExtensionsBlock[]>([]);
  const initialValues = React.useMemo(() => {
    return { blocklist };
  }, [blocklist]);

  function getBlocklist() {
    const blockUrls = props.settings.dashExtServerSettings.blocklist.map((v: string) => {
      const blockUrl: ExtensionsBlock = {
        blockUrl: v,
      };
      return blockUrl;
    });
    setBlocklist(blockUrls);
  }

  // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => getBlocklist(), []);

  const handleSaveAction = React.useCallback(
    async (values: ExtensionsServerFormField, formik: ExtensionsServerFormikProps) => {
      const blocklist = values.blocklist.map((extensionsBlock) => extensionsBlock.blockUrl);
      props.settings.dashExtServerSettings.blocklist = blocklist;
      getBlocklist();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialValues.blocklist]
  );

  return (
      <SettingsSection title={Messages.extensionsSettings_header()}
                       testId={SERVER_EXTENSIONS_SECTION_TEST_ID}
                       description={
                        <div>
                          {Messages.extensionsServerSettings_descriptionWhat()}
                          <TextLinkWidget testId={SERVER_EXTENSIONS_LEARN_MORE_LINK_TEST_ID}
                                          target={'_blank'}
                                          className={learnMoreStyle}
                                          href={HelpService.getServerHelpUrlByCategory('extensions')}>
                            {Messages.learnMore()}
                          </TextLinkWidget>
                        </div>
                      }>

        <FormikCheckbox name={EXTENSIONS_ENABLED_FIELD}
                        label={Messages.extensionsServerSettings_allowForServer()}
                        testId={SERVER_EXTENSIONS_ENABLED_TEST_ID}>
        </FormikCheckbox>
        <div className={sectionHeader}>{Messages.extensionsServerSettings_blockListHeader()}</div>
        <Formik initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={(values: ExtensionsServerFormField, formik: ExtensionsServerFormikProps) => handleSaveAction(values, formik)}>
          <BlockList />
        </Formik>
      </SettingsSection>
  );
}

export function handleDeleteClient(idx: number, formik: ExtensionsServerFormikProps) {
  if (idx > -1) {
    const clients = [...formik.values.blocklist];
    clients.splice(idx, 1);
    formik.setFieldValue('blocklist', clients, false);
  }
}

export function handleAddClient(formik: ExtensionsServerFormikProps) {
  const clients = [...formik.values.blocklist];
  clients.unshift({
    blockUrl: DEFAULT_URL,
  });
  formik.setFieldValue('blocklist', clients, false);
}

export function handleUrlChange(idx: number, formik: ExtensionsServerFormikProps, event: React.ChangeEvent<HTMLInputElement>) {
  const clients = [...formik.values.blocklist];
  clients[idx] = {...clients[idx], blockUrl: event.target.value};
  formik.setFieldValue('blocklist', clients, false);
}

function BlockList() {
  useSettingsCoordinator();
  const formik = useFormikContext<ExtensionsServerFormField>();

  enum BlockListDataKeys {
    BlockUrl = 'blockUrl',
  }

  const columns: DataGrid.Column[] = [];
  columns.push({
    name: BlockListDataKeys.BlockUrl,
    dataKey: BlockListDataKeys.BlockUrl,
    headerText: Messages.extensionsSettings_list_URLHeader(),
    headerFormatter: (_) => {
      return <span className={addButtonInfoStyle}>
        {Messages.extensionsSettings_list_URLHeader()}
        <ButtonWidget buttonType={ButtonType.Outline}
          testId={ADD_BLOCK_URL_BUTTON_TEST_ID}
          className={`${buttonStyle} ${addButtonStyle}`}
          onClick={() => handleAddClient(formik) }
          disabled={false}
          type='button'>{Messages.extensionsSettings_addUrlButton()}
        </ButtonWidget>
        <QuickTooltip
          testId={ADD_BLOCK_URL_TOOLTIP_TEST_ID}
          content={Messages.extensionsServerSettings_blocklist_URLHeaderInfo()} />
      </span>;
    },
    growToFit: true,
    resizable: false,
    sortable: false,
    cellFormatter: (props) => {
      return <TextFieldWidget testId={`${BLOCK_URL_INPUT_PREFIX_TEST_ID}${props.rowIdx}`}
        className={`${wideField} ${textFieldStyle}`}
        value={props.row.data[BlockListDataKeys.BlockUrl]}
        required={true}
        label={null}
        onChange={(event) => handleUrlChange(props.rowIdx, formik, event)}
      />;
    },
  });
  columns.push({
    name: '',
    dataKey: '',
    headerText: '',
    resizable: false,
    sortable: false,
    cellFormatter: (props) => {
      return <ButtonWidget buttonType={ButtonType.LowEmphasis}
        testId={`${DELETE_BLOCK_URL_BUTTON_PREFIX_TEST_ID}${props.rowIdx}`}
        className={buttonStyle}
        onClick={() => handleDeleteClient(props.rowIdx, formik)}
        disabled={false}
        type='button'>{Messages.deleteMessage()}
      </ButtonWidget>;
    }
  });

  let rows: DataGrid.Row[] = [];
  let rowIndex = 0;
  formik.values.blocklist.forEach(rowData => {
    let rowItemData: { [key: string]: {} } = {};
    rowItemData[BlockListDataKeys.BlockUrl] = rowData.blockUrl;
    rows.push({
      key: rowIndex.toString(),
      data: rowItemData,
    });
    rowIndex++;
  });

  return <div>
    <div className={`tb-spaces-list-view ${dataGridContainerStyle} ${dataGridBlockContainerStyle}`}>
      <DataGrid
        config= {{
          rowOuterHeight: dataGridRowHeight,
          rememberLastScrollPos: true,
        }}
        emptyChild={
          <div data-tb-test-id={BLOCKLIST_EMPTY_TEST_ID} className="tb-data-grid-cell-text tb-data-grid-bridge-center">
            {Messages.extensionsServerSettings_blocklist_emptyMessage()}
          </div>
        }
        cols={columns}
        rowStore={{
          getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
            rowHandler(rows);
          }
        }}
        showVerticalGridLines={false}
        showHorizontalGridLines={false}
        showRowBanding={true}
      />
    </div>
  </div>;
}

export const ExtensionsServerSectionSpecs = componentRegistryEntry({
  name: 'ExtensionsServerSection',
  component: ExtensionsServerSection,
  onSubmit: async (settings: ExtensionsSettingsSubspaceState) => {
    await Promise.all([ServerSettingsInstance.updateExtensionsSettings(settings.dashExtServerSettings)]);
  },
});
